/* eslint-disable jsx-a11y/iframe-has-title */
import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { modulesAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import Card from '../components/UIKit/Card';
import Button from '../components/UIKit/Button';
import DeleteConfirmationModal from '../components/DeleteConfirmationModal';
import {
  ArrowBack as ArrowBackIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Person as UserIcon,
  CalendarToday as CalendarIcon,
  MenuBook as BookOpenIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  EmojiEvents as AchievementIcon
} from '@mui/icons-material';
import { 
  Container,
  Typography,
  Box,
  Grid,
  Chip,
  CircularProgress,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';

const ModuleDetail = () => {
  const { t } = useTranslation('modules');
  const navigate = useNavigate();
  const { id } = useParams();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [module, setModule] = useState(null);
  const [deleteModal, setDeleteModal] = useState({
    open: false,
    loading: false,
    canDelete: false,
    courses: [],
    isForceDelete: false
  });

  // Загрузка модуля
  useEffect(() => {
    const loadModule = async () => {
      try {
        setLoading(true);
        const response = await modulesAPI.getById(id);
        setModule(response.data);
      } catch (error) {
        console.error('Ошибка при загрузке модуля:', error);
        toast.error(t('modulesPage.errors.loadError'));
        // Не перенаправляем на список модулей, остаемся на странице
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      loadModule();
    }
  }, [id, t]);

  // Проверка прав на редактирование
  const canEdit = () => {
    if (!user || !module) return false;
    return user.userMeta?.roles?.includes('admin') || 
           user.userMeta?.roles?.includes('teacher') ||
           module.author?._id === user.id;
  };

  // Открытие модального окна удаления
  const handleDeleteClick = async () => {
    try {
      // Проверяем возможность удаления
      // Используем реальный ObjectId модуля для проверки удаления, а не id из URL
      const checkResponse = await modulesAPI.checkDelete(module._id);
      const { canDelete, coursesUsingModule } = checkResponse.data;

      setDeleteModal({
        open: true,
        loading: false,
        canDelete,
        courses: coursesUsingModule,
        isForceDelete: false
      });
    } catch (error) {
      console.error('Ошибка при проверке возможности удаления:', error);
      toast.error(t('modulesPage.errors.loadError'));
    }
  };

  // Подтверждение удаления
  const handleConfirmDelete = async () => {
    try {
      setDeleteModal(prev => ({ ...prev, loading: true }));

      if (!deleteModal.canDelete && !deleteModal.isForceDelete) {
        // Показываем второе предупреждение для принудительного удаления
        setDeleteModal(prev => ({ ...prev, isForceDelete: true, loading: false }));
        return;
      }

      // Выполняем удаление
      // Используем реальный ObjectId модуля для удаления, а не id из URL
      await modulesAPI.delete(module._id, true, deleteModal.isForceDelete);
      
      toast.success(deleteModal.isForceDelete 
        ? t('deleteConfirmation.forceDeleteMessage') 
        : t('deleteConfirmation.message')
      );

      setDeleteModal(prev => ({ ...prev, open: false }));
      navigate('/modules');
    } catch (error) {
      console.error('Ошибка при удалении модуля:', error);
      
      if (error.response?.data?.message) {
        toast.error(error.response.data.message);
      } else {
        toast.error(t('modulesPage.errors.loadError'));
      }
      
      setDeleteModal(prev => ({ ...prev, loading: false }));
    }
  };

  // Закрытие модального окна
  const handleCloseDeleteModal = () => {
    setDeleteModal({
      open: false,
      loading: false,
      canDelete: false,
      courses: [],
      isForceDelete: false
    });
  };

  // Форматирование даты
  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('ru-RU', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };


  if (loading) {
    return (
      <Container>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (!module) {
    return (
      <Container>
        <Box sx={{ textAlign: 'center', py: 8 }}>
          <Typography variant="h4" component="h1" gutterBottom>
            {t('modulesPage.noModules.title')}
          </Typography>
          <Button variant="contained" onClick={() => navigate('/modules')}>
            {t('modulesPage.backToList')}
          </Button>
        </Box>
      </Container>
    );
  }

  return (
    <Container>
      <Box sx={{ my: 4 }}>
        {/* Заголовок */}
        <Box sx={{ mb: 4 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
            <Button
              variant="outlined"
              startIcon={<ArrowBackIcon />}
              onClick={() => navigate('/modules')}
            >
              {t('modulesPage.backToList')}
            </Button>
            <Typography variant="h3" component="h1" sx={{ flex: 1 }}>
              {module.title}
            </Typography>
            {/* Действия в правом верхнем углу */}
            {canEdit() && (
              <Box sx={{ display: 'flex', gap: 1, ml: 'auto' }}>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<EditIcon />}
                  component={Link}
                  to={`/modules/${id}/edit`}
                >
                  {t('modulesPage.actions.edit')}
                </Button>
                <Button
                  variant="outlined"
                  color="error"
                  size="small"
                  startIcon={<DeleteIcon />}
                  onClick={handleDeleteClick}
                >
                  {t('modulesPage.actions.delete')}
                </Button>
              </Box>
            )}
            {module.isDraft && (
              <Chip
                label={t('modulesPage.status.draft')}
                color="warning"
                variant="outlined"
                icon={<VisibilityOffIcon />}
              />
            )}
          </Box>
        </Box>

        <Grid container spacing={3}>
          {/* Основная информация */}
          <Grid item xs={12} lg={8}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              {/* Описание */}
              {module.description && (
                <Card elevation={2}>
                  <Box sx={{ p: 3 }}>
                    <Typography variant="h5" component="h2" gutterBottom>
                      {t('moduleDetail.description')}
                    </Typography>
                    <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.6 }}>
                      {module.description}
                    </Typography>
                  </Box>
                </Card>
              )}

              {/* Видео */}
              {module.videoUrl && (
                <Card elevation={2}>
                  <Box sx={{ p: 3 }}>
                    <Typography variant="h5" component="h2" gutterBottom>
                      {t('moduleDetail.video')}
                    </Typography>
                    <Box sx={{ position: 'relative', paddingBottom: '56.25%', height: 0, overflow: 'hidden' }}>
                      <iframe
                        src={module.videoUrl.replace('watch?v=', 'embed/')}
                        style={{
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          width: '100%',
                          height: '100%',
                          border: 'none'
                        }}
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                        allowFullScreen
                      ></iframe>
                    </Box>
                  </Box>
                </Card>
              )}

              {/* Содержимое */}
              {module.content && (
                <Card elevation={2}>
                  <Box sx={{ p: 3 }}>
                    <Typography variant="h5" component="h2" gutterBottom>
                      {t('moduleDetail.content')}
                    </Typography>
                    <Box
                      sx={{
                        '& .prose': { maxWidth: 'none' },
                        '& h1, & h2, & h3, & h4, & h5, & h6': {
                          color: 'text.primary',
                          fontWeight: 'bold',
                          mb: 1
                        },
                        '& p': {
                          color: 'text.secondary',
                          lineHeight: 1.6,
                          mb: 2
                        },
                        '& ul, & ol': {
                          color: 'text.secondary',
                          pl: 2
                        },
                        '& li': {
                          mb: 0.5
                        }
                      }}
                      dangerouslySetInnerHTML={{ __html: module.content }}
                    />
                  </Box>
                </Card>
              )}

              {/* Уроки */}
              {module.lessons && module.lessons.length > 0 && (
                <Card elevation={2}>
                  <Box sx={{ p: 3 }}>
                    <Typography variant="h5" component="h2" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                      <BookOpenIcon sx={{ mr: 1 }} />
                      {t('moduleDetail.lessons')} ({module.lessons.length})
                    </Typography>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                      {module.lessons.map((lesson, index) => (
                        <Button
                          key={lesson._id || index}
                          component={Link}
                          to={`/lessons/${lesson.slug || lesson._id}`}
                          variant="outlined"
                          sx={{ 
                            p: 2, 
                            justifyContent: 'flex-start',
                            textAlign: 'left',
                            border: '1px solid',
                            borderColor: 'divider',
                            borderRadius: 1,
                            bgcolor: 'background.paper',
                            transition: 'all 0.2s ease-in-out',
                            '&:hover': {
                              borderColor: 'primary.main',
                              bgcolor: 'primary.50',
                              transform: 'translateY(-1px)',
                              boxShadow: 1
                            }
                          }}
                        >
                          <Box sx={{ width: '100%' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                              <BookOpenIcon sx={{ fontSize: '1rem', color: 'primary.main' }} />
                              <Typography variant="subtitle1" fontWeight="medium" color="primary">
                                {lesson.title || `${t('moduleDetail.lesson')} ${index + 1}`}
                              </Typography>
                            </Box>
                            {lesson.description && (
                              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, pl: 3 }}>
                                {lesson.description}
                              </Typography>
                            )}
                          </Box>
                        </Button>
                      ))}
                    </Box>
                  </Box>
                </Card>
              )}

            </Box>
          </Grid>

          {/* Боковая панель */}
          <Grid item xs={12} lg={4}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              {/* Информация о модуле */}
              <Card elevation={2}>
                <Box sx={{ p: 3 }}>
                  <Typography variant="h6" component="h3" gutterBottom>
                    {t('moduleDetail.info')}
                  </Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <UserIcon sx={{ fontSize: '1rem', color: 'text.secondary' }} />
                      <Typography variant="body2" color="text.secondary">
                        {t('common:author')}:
                      </Typography>
                      <Typography variant="body2" fontWeight="medium">
                        {module.author?.userMeta?.fullName || t('moduleCard.noAuthor')}
                      </Typography>
                    </Box>
                    
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <CalendarIcon sx={{ fontSize: '1rem', color: 'text.secondary' }} />
                      <Typography variant="body2" color="text.secondary">
                        {t('moduleDetail.created')}:
                      </Typography>
                      <Typography variant="body2" fontWeight="medium">
                        {formatDate(module.createdAt)}
                      </Typography>
                    </Box>

                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <CalendarIcon sx={{ fontSize: '1rem', color: 'text.secondary' }} />
                      <Typography variant="body2" color="text.secondary">
                        {t('moduleDetail.updated')}:
                      </Typography>
                      <Typography variant="body2" fontWeight="medium">
                        {formatDate(module.updatedAt)}
                      </Typography>
                    </Box>

                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {module.isDraft ? (
                        <VisibilityOffIcon sx={{ fontSize: '1rem', color: 'warning.main' }} />
                      ) : (
                        <VisibilityIcon sx={{ fontSize: '1rem', color: 'success.main' }} />
                      )}
                      <Typography variant="body2" color="text.secondary">
                        {t('moduleDetail.status')}:
                      </Typography>
                      <Typography 
                        variant="body2" 
                        fontWeight="medium"
                        color={module.isDraft ? 'warning.main' : 'success.main'}
                      >
                        {module.isDraft ? t('modulesPage.status.draft') : t('modulesPage.status.published')}
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              </Card>

              {/* Курсы, где используется модуль */}
              {module.courses && module.courses.length > 0 && (
                <Card elevation={2}>
                  <Box sx={{ p: 3 }}>
                    <Typography variant="h6" component="h3" gutterBottom>
                      {t('moduleDetail.usedInCourses')}
                    </Typography>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                      {module.courses.map((course) => (
                        <Button
                          key={course._id}
                          component={Link}
                          to={`/courses/${course.slug || course._id}`}
                          variant="outlined"
                          size="small"
                          sx={{ 
                            justifyContent: 'flex-start',
                            textAlign: 'left',
                            p: 2,
                            '&:hover': {
                              bgcolor: 'primary.50',
                              borderColor: 'primary.main'
                            }
                          }}
                        >
                          <Box>
                            <Typography variant="body2" fontWeight="medium" color="primary">
                              {course.title}
                            </Typography>
                            {course.description && (
                              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                                {course.description}
                              </Typography>
                            )}
                          </Box>
                        </Button>
                      ))}
                    </Box>
                  </Box>
                </Card>
              )}

              {/* Достижения */}
              {module.achievements && module.achievements.length > 0 && (
                <Card elevation={2}>
                  <Box sx={{ p: 3 }}>
                    <Typography variant="h6" component="h3" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                      <AchievementIcon sx={{ mr: 1 }} />
                      {t('moduleDetail.achievements')}
                    </Typography>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                      {module.achievements.map((achievement, index) => (
                        <Box 
                          key={achievement._id || index} 
                          sx={{ 
                            p: 2, 
                            border: '1px solid',
                            borderColor: 'warning.main',
                            borderRadius: 1,
                            bgcolor: 'warning.50',
                            transition: 'all 0.2s ease-in-out',
                            '&:hover': {
                              borderColor: 'warning.dark',
                              bgcolor: 'warning.100',
                              transform: 'translateY(-1px)',
                              boxShadow: 1
                            }
                          }}
                        >
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                            <AchievementIcon sx={{ fontSize: '1rem', color: 'warning.main' }} />
                            <Typography variant="subtitle2" fontWeight="medium" color="warning.dark">
                              {achievement.title || `${t('moduleDetail.achievement')} ${index + 1}`}
                            </Typography>
                          </Box>
                          {achievement.description && (
                            <Typography variant="body2" color="warning.dark" sx={{ mt: 0.5, pl: 3 }}>
                              {achievement.description}
                            </Typography>
                          )}
                        </Box>
                      ))}
                    </Box>
                  </Box>
                </Card>
              )}
            </Box>
          </Grid>
        </Grid>

        {/* Модальное окно подтверждения удаления */}
        <DeleteConfirmationModal
          open={deleteModal.open}
          onClose={handleCloseDeleteModal}
          onConfirm={handleConfirmDelete}
          title={`${t('deleteConfirmation.title')} "${module?.title}"`}
          message={
            deleteModal.canDelete
              ? t('deleteConfirmation.message')
              : deleteModal.isForceDelete
              ? t('deleteConfirmation.forceDeleteMessage')
              : t('deleteConfirmation.warningMessage')
          }
          courses={deleteModal.courses}
          isForceDelete={deleteModal.isForceDelete}
          loading={deleteModal.loading}
        />
      </Box>
    </Container>
  );
};

export default ModuleDetail;
